import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeepLinkContext } from "@/hooks/tauri/DeepLinkProvider";
import { TAURI_EVENTS, type DeepLinkDetectedEventPayload } from "@/infra/tauri/events";
import { attemptDeepLinkNavigation, handleDeepLinkHomeRedirect } from "@/infra/tauri/deepLinks";
import { loggerService } from "@akina/infra";
import { isApp } from "@akina/utils";
import { invoke } from "@tauri-apps/api/core";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { onOpenUrl } from "@tauri-apps/plugin-deep-link";
import { platform } from "@tauri-apps/plugin-os";

const LOG_SCOPE = "[deep-links]";

const getLog = (message: string) => `${LOG_SCOPE} ${message}`;

const LOG_MESSAGES = {
  CHECKING_STARTUP_DEEP_LINK: getLog("Checking startup deep link..."),
  OPENED_EMPTY_ROUTE: getLog("Empty route detected, redirecting to home"),
  REDIRECTING_TO_HOME: getLog("Redirecting to home"),
  INITIALIZATION_ERROR: (error: string) => getLog(`Failed to initialize deep link listener: ${error}`),
  DEEP_LINK_DETECTED: (url: string) => getLog(`Deep link detected: ${url}`),
  STARTUP_DEEP_LINK: (url: string) => getLog(`Startup deep link detected: ${url}`),
  ADDITIONAL_DEEP_LINKS: (count: number) => getLog(`Found ${count} additional deep links`),
} as const;

/**
 * The main hook that manages deep link state
 */
const useDeepLinkState = (): {
  isInitialized: boolean;
  setIsInitialized: (callback?: () => void) => void;
} => {
  const { isDeepLinkHandled, handleDeepLink } = useDeepLinkContext();

  return {
    isInitialized: isDeepLinkHandled,
    setIsInitialized: handleDeepLink,
  };
};

/**
 * Check for deep links that were used to start the app
 */
const useCheckStartupDeepLink = (deepLinkState: ReturnType<typeof useDeepLinkState>) => {
  const navigate = useNavigate();
  const { setIsInitialized } = deepLinkState;
  const route = useLocation();
  const isInitialCheckRef = useRef(false);

  useEffect(() => {
    if (isInitialCheckRef.current) return;
    isInitialCheckRef.current = true;

    // This check is needed for auth0 callback redirect that is on the empty root as well (e.g. care/?code=...)
    if (hasAuthCallbackParams(route)) {
      setIsInitialized();
      return;
    }

    const checkStartupDeepLink = async () => {
      if (!isApp) {
        setIsInitialized();
        return;
      }

      try {
        loggerService.info(LOG_MESSAGES.CHECKING_STARTUP_DEEP_LINK);
        const startupUrl = await invoke<string | null>("get_startup_deep_link");

        if (startupUrl) {
          attemptDeepLinkNavigation(navigate, setIsInitialized, startupUrl);
        } else {
          handleDeepLinkHomeRedirect(navigate, setIsInitialized, LOG_MESSAGES.REDIRECTING_TO_HOME);
        }
      } catch (error) {
        loggerService.error(
          `Failed to check startup deep link: ${error}, route: ${route.pathname}, hasAuthCallbackParams: ${hasAuthCallbackParams(route)}`,
        );
        handleDeepLinkHomeRedirect(navigate, setIsInitialized, LOG_MESSAGES.REDIRECTING_TO_HOME);
      }
    };

    checkStartupDeepLink();
  }, [navigate, setIsInitialized, route.pathname]);
};

/**
 * Poll for additional deep links that might come in during runtime for macos
 */
const usePollForAdditionalDeepLinks = () => {
  const navigate = useNavigate();
  const { setIsInitialized } = useDeepLinkState();
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isApp) return;

    // Set up polling interval - especially important for Safari/macOS
    const checkAdditionalDeepLinks = async () => {
      try {
        const additionalLinks = await invoke<string[]>("get_additional_deep_links");

        if (additionalLinks && additionalLinks.length > 0) {
          loggerService.info(LOG_MESSAGES.ADDITIONAL_DEEP_LINKS(additionalLinks.length));

          // Process each deep link in order
          for (const link of additionalLinks) {
            attemptDeepLinkNavigation(navigate, setIsInitialized, link);
          }
        }
      } catch (error) {
        loggerService.error(`Failed to poll for additional deep links: ${error}`);
      }
    };

    // Poll every 1 second
    intervalRef.current = window.setInterval(checkAdditionalDeepLinks, 1000);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [navigate, setIsInitialized]);
};

/**
 * Listen for deep links on Windows by listening to the event emitted by RUST backend.
 */
function useListenForDeepLinksOnWindows() {
  const navigate = useNavigate();
  const { setIsInitialized } = useDeepLinkState();
  const listenerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isApp) return;
    if (platform() !== "windows") return;

    // Skip if we already have a listener
    if (listenerRef.current) return;

    const window = getCurrentWindow();

    const listen = async () => {
      try {
        const unlisten = await window.listen<DeepLinkDetectedEventPayload>(TAURI_EVENTS.DEEP_LINK_DETECTED, event => {
          loggerService.info(LOG_MESSAGES.DEEP_LINK_DETECTED(event.payload.url));
          attemptDeepLinkNavigation(navigate, setIsInitialized, event.payload.url);
        });

        // Store the unlisten function for cleanup
        listenerRef.current = unlisten;
      } catch (error) {
        loggerService.error(LOG_MESSAGES.INITIALIZATION_ERROR(error as string));
        handleDeepLinkHomeRedirect(navigate, setIsInitialized, LOG_MESSAGES.REDIRECTING_TO_HOME);
      }
    };

    listen();

    // Cleanup function to remove the listener
    return () => {
      if (listenerRef.current) {
        listenerRef.current();
        listenerRef.current = null;
      }
    };
  }, [navigate, setIsInitialized]);
}

/**
 * Listen for deep links on MacOS with onOpenUrl
 */
function useListenForDeepLinksOnMacOS() {
  const navigate = useNavigate();
  const { setIsInitialized } = useDeepLinkState();
  const isSetupRef = useRef(false);

  useEffect(() => {
    if (!isApp) return;
    if (platform() !== "macos") return;

    // Skip if already set up
    if (isSetupRef.current) return;

    const setupDeepLink = async () => {
      try {
        await onOpenUrl((urls: string[]) => {
          loggerService.info(LOG_MESSAGES.DEEP_LINK_DETECTED(urls[0]));

          const url = urls[0];
          attemptDeepLinkNavigation(navigate, setIsInitialized, url);
        });

        // Mark as set up
        isSetupRef.current = true;
      } catch (error) {
        loggerService.error(LOG_MESSAGES.INITIALIZATION_ERROR(error as string));
        handleDeepLinkHomeRedirect(navigate, setIsInitialized, LOG_MESSAGES.REDIRECTING_TO_HOME);
      }
    };

    setupDeepLink();

    // No cleanup needed for onOpenUrl as it doesn't return an unlisten function
    // But we'll still return an empty function for consistency
    return () => {};
  }, [navigate, setIsInitialized]);
}

/**
 * - Handle the empty root route by redirecting to home after initialization
 * - Handles auth0 callback redirect (e.g. localhost:5173/?code=...&state=...)
 */
function useHandleEmptyRoute(deepLinkState: ReturnType<typeof useDeepLinkState>) {
  const navigate = useNavigate();
  const { setIsInitialized } = useDeepLinkState();
  const route = useLocation();
  const timeoutRef = useRef<number | null>(null);
  const { isInitialized } = deepLinkState;

  useEffect(() => {
    if (!isInitialized) return;

    if (route.pathname === "/" && !hasAuthCallbackParams(route)) {
      if (!isApp) handleDeepLinkHomeRedirect(navigate, setIsInitialized, LOG_MESSAGES.REDIRECTING_TO_HOME);

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        loggerService.info(LOG_MESSAGES.OPENED_EMPTY_ROUTE);
        handleDeepLinkHomeRedirect(navigate, setIsInitialized, LOG_MESSAGES.REDIRECTING_TO_HOME);
      }, 1000);
    }

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [isInitialized, route, navigate, setIsInitialized]);
}

// Helper function to detect if the current URL contains auth callback parameters
function hasAuthCallbackParams(location: ReturnType<typeof useLocation>): boolean {
  const searchParams = new URLSearchParams(location.search);
  return searchParams.has("code") && searchParams.has("state");
}

function useDeepLinksListener() {
  const deepLinkState = useDeepLinkState();

  useCheckStartupDeepLink(deepLinkState);
  useListenForDeepLinksOnWindows();
  useListenForDeepLinksOnMacOS();
  usePollForAdditionalDeepLinks();
  useHandleEmptyRoute(deepLinkState);
}

export { useDeepLinksListener };
